'use client';

import * as React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Loader2 } from 'lucide-react';

import { Modal } from '@/components/antd-wrapper/modal/global/modal';
import { Form } from '@/components/ui/form';
import {
  getNicknameRules,
  CHANGE_NICKNAME_VALIDATE_TRIGGER,
  getChangeNicknamePlaceholder,
} from '@/lib/utils/form';
import { DmInput } from '../../custom-form/dm-input';
import { IUpdateUserInfoParams } from '@/services/user';
import { Button } from 'antd';
import { useI18n } from '@/hooks/use-i18n';

// 定义表单验证模式
const getFormSchema = () =>
  z.object({
    username: getNicknameRules(),
  });

type FormValues = z.infer<ReturnType<typeof getFormSchema>>;

interface EditUserNameModalProps {
  updateUserInfo: (data: IUpdateUserInfoParams) => Promise<void>;
  open: boolean;
  onClose: () => void;
  userInfo: USER.Profile;
}

const EditUserNameModal = ({
  open,
  onClose,
  updateUserInfo,
  userInfo,
}: EditUserNameModalProps) => {
  const { t } = useI18n(['components', 'common']);
  const form = useForm<FormValues>({
    resolver: zodResolver(getFormSchema()),
    defaultValues: {
      username: userInfo.name,
    },
  });

  const userName = form.getValues('username')?.trim();

  const onSubmit = async (username: string) => {
    await updateUserInfo({ username });
    onClose();
  };

  const handleSubmit = async (values: FormValues) => {
    // 如果昵称没有变化，直接关闭对话框
    if (values.username.trim() === userInfo.name) {
      onClose();
      return;
    }
    try {
      await onSubmit(values.username);
      onClose();
    } catch (error) {
      console.error('Failed to update username:', error);
    }
  };
  const isError = !!form.formState.errors.username;

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={
        <span className="text-base color-[#000000BF] text-[18px] font-semibold text-black">
          {t('components:sidebar.editNickname.title')}
        </span>
      }
      width={400}
      height={240}
      footer={null}
      className="edit-user-name-modal"
      classNames={{
        content: '!pb-[0]',
      }}
    >
      <div className="flex flex-col space-y-4 px-11 pt-4 pb-11">
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleSubmit)}
            className="space-y-4"
          >
            <DmInput
              form={form}
              name="username"
              placeholder={getChangeNicknamePlaceholder()}
              trigger={CHANGE_NICKNAME_VALIDATE_TRIGGER}
            />

            <Button
              type="primary"
              loading={form.formState.isSubmitting}
              htmlType="submit"
              disabled={form.formState.isSubmitting || isError || !userName}
              className="h-11 w-full text-[16px] font-semibold"
            >
              {!form.formState.isSubmitting ? (
                <>{t('common:confirm')}</>
              ) : (
                <>{t('common:saving')}</>
              )}
            </Button>
          </form>
        </Form>
      </div>
    </Modal>
  );
};

export default EditUserNameModal;
